import { useTranslation } from "react-i18next";
import { Group, Loader, Modal, Stack, Text, TextInput } from "@mantine/core";
import { isEmail, useForm } from "@mantine/form";
import { useCaptcha } from "../api/captcha";
import type { ApiError } from "../api/client";
import { useForgotPassword } from "../api/queries";
import { Chip } from "./Chip";
import { FeedbackLine } from "./FeedbackLine";

/**
 * Ask for a reset link.
 *
 * Deliberately the same shape as `RegisterDialog`: an address, a captcha the server asks for
 * only if it wants one, and the server's own sentence on success. It never says whether the
 * address has an account -- that answer would make this form a membership oracle -- so there
 * is nothing to show but what came back.
 */
export function ForgotDialog({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const { t } = useTranslation();
  const forgot = useForgotPassword();
  const { guard, verifying, failed } = useCaptcha();
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { email: "" },
    // `isEmail` rejects the empty string too, so one rule covers required and malformed.
    validate: { email: isEmail(t("registerDialog.emailInvalid")) },
  });

  return (
    <Modal opened onClose={onClose} title={t("forgotDialog.heading")}>
      {forgot.isSuccess ? (
        <Stack gap="sm">
          <Text size="sm">{forgot.data.detail}</Text>
          <Group justify="flex-end" gap="xs">
            <Chip onClick={onClose}>{t("common.close")}</Chip>
          </Group>
        </Stack>
      ) : (
        // `noValidate` for the same reason the register form has it: the field keeps
        // `type="email"` for the phone keyboard without the browser refusing the submit in
        // its own language rather than the catalogue's.
        <form
          noValidate
          onSubmit={form.onSubmit(
            ({ email }) =>
              void guard((captcha_token) =>
                forgot.mutateAsync(captcha_token ? { email, captcha_token } : { email }),
              ),
          )}
        >
          <Stack gap="sm">
            <Text size="sm">{t("forgotDialog.intro")}</Text>
            <TextInput
              data-autofocus
              type="email"
              label={t("registerDialog.email")}
              name="email"
              autoComplete="email"
              key={form.key("email")}
              {...form.getInputProps("email")}
            />

            {/* One slot, three states. The captcha's own "required" refusal never reaches the
                user: it is an instruction to this component, not a message. */}
            <FeedbackLine role={verifying ? "status" : "alert"} c={verifying ? "dimmed" : "danger"}>
              {verifying ? (
                <Group gap="xs" wrap="nowrap">
                  <Loader size={14} />
                  <Text inherit>{t("captcha.verifying")}</Text>
                </Group>
              ) : failed ? (
                t("captcha.failed")
              ) : (
                forgot.isError &&
                (forgot.error as ApiError).captcha == null &&
                (forgot.error as Error).message
              )}
            </FeedbackLine>

            <Group justify="flex-end" gap="xs">
              <Chip onClick={onLogin}>{t("forgotDialog.backToLogin")}</Chip>
              <Chip type="submit" variant="accent" disabled={forgot.isPending || verifying}>
                {forgot.isPending || verifying
                  ? t("registerDialog.sending")
                  : t("forgotDialog.submit")}
              </Chip>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
